import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BracketResultRenderer from '../../components/bracket/BracketResultRenderer';

const safeJsonParse = (raw, fallback) => {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

const decodeBase64Url = (s) => {
  try {
    const base64 = (s || '').replace(/-/g, '+').replace(/_/g, '/');
    let padded = base64;
    while (padded.length % 4 !== 0) padded += '=';

    const bin = atob(padded);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const json = new TextDecoder('utf-8').decode(bytes);
    return safeJsonParse(json, null);
  } catch (e) {
    return null;
  }
};

const PublicBracketPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const data = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const encoded = params.get('data');
    return decodeBase64Url(encoded);
  }, [location.search]);

  const generated = data?.result?.generated;
  const config = data?.config || {};

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-card border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center gap-3 transition-athletic hover:opacity-80"
          >
            <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center">
              <Icon name="Dumbbell" size={24} color="var(--color-primary)" />
            </div>
            <span className="text-lg md:text-xl font-heading font-semibold text-foreground">PickleLink</span>
          </button>

          <Button
            variant="ghost"
            size="sm"
            iconName="Shield"
            iconPosition="left"
            onClick={() => navigate('/admin-login')}
          >
            관리자
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-10">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">{data?.name || '대진표'}</h1>
            <p className="text-sm caption text-muted-foreground mt-2">공개 페이지</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" iconName="ArrowLeft" iconPosition="left" onClick={() => navigate('/')}
            >
              홈
            </Button>
          </div>
        </div>

        {!data ? (
          <div className="bg-muted border border-border rounded-xl p-6">
            <p className="text-sm text-foreground font-medium">대진표 데이터를 불러올 수 없습니다.</p>
            <p className="text-xs caption text-muted-foreground mt-2">링크가 손상되었거나 만료되었을 수 있습니다.</p>
          </div>
        ) : !generated ? (
          <div className="bg-muted border border-border rounded-xl p-6">
            <p className="text-sm text-foreground font-medium">아직 생성된 대진 결과가 없습니다.</p>
            <p className="text-xs caption text-muted-foreground mt-2">대진 생성 후 공개 페이지를 열어주세요.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {generated?.teams?.length > 0 && (
              <div className="bg-card border border-border rounded-xl p-4">
                <h2 className="text-base font-heading font-semibold text-foreground mb-3">팀 목록</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {generated.teams.map((t, idx) => (
                    <div key={t.id} className="p-2 rounded-md bg-muted border border-border">
                      <p className="text-sm text-foreground">
                        {idx + 1}. {t.a.name} / {t.b.name}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <BracketResultRenderer generated={generated} config={config} />

            <div className="bg-muted border border-border rounded-xl p-4 flex items-start gap-2">
              <Icon name="Info" size={18} className="text-muted-foreground mt-0.5 flex-shrink-0" />
              <p className="text-xs caption text-muted-foreground">
                이 페이지는 링크(URL)에 대진표 데이터를 포함해 표시합니다. 링크 길이가 길어질 수 있습니다.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PublicBracketPage;
